// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动

import { VAxios } from './axios';
import { AxiosTransform } from './axios-transform';
import axios from 'axios';
import qs from 'qs';
// import { checkStatus } from './check-status'; 使用后端返回错误信息
import { XrkMessage, XrkMessageBox } from 'xrk-components';
import {
  RequestEnum,
  ResultCodeEnum,
  ResultStatusEnum,
  ContentTypeEnum
} from './enums';
import { isString } from '@/utils/is/index';
import { setObjToUrlParams, formatParamsToPath } from './url';
import { RequestOptions } from './types';
import router from '@/router';
import { TOKEN_KEY, REQUEST_PATH, REQUEST_DOMAIN, PLATFORM_ID } from '@/config';
import appStore from '@/store';
import { storeToRefs } from 'pinia';
import { PLATFORM_PORT } from '@constants/system';
import { readAsTextCust } from '@/utils/file';

const singletonLogout = (() => {
  let flag = false;
  return () => {
    if (!flag) {
      flag = true;
      // 到登录页
      XrkMessageBox.confirm('登录身份已失效,请重新登录!', '提示', {
        confirmButtonText: '确定',
        showClose: false,
        showCancelButton: false,
        center: true
      })
        .then(() => {
          appStore.useUserStoreSetup.clearUser({
            redirect: router.currentRoute.value.fullPath
          });
        })
        .finally(() => {
          flag = false;
          window.location.reload(); // 主要为了清空弹层组件
        });
    }
  };
})();

const isDev = process.env.NODE_ENV === 'development';
const isProd = process.env.NODE_ENV === 'production';
/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    const {
      apiUrl,
      joinParamsToUrl,
      joinParamsToPath,
      isParseToJson,
      yapi,
      headers
    } = options;
    config.url = isDev ? `${config.url}` : `${apiUrl || ''}${config.url}`;
    if (headers) {
      config.headers = { ...(config.headers || {}), ...headers };
    }
    // 使用yapi
    if (yapi && isDev) {
      config.baseURL = `//yapi.jimijiayuan.cn/mock/${yapi}${REQUEST_PATH}`;
    }
    if (isProd) {
      config.baseURL = REQUEST_DOMAIN + REQUEST_PATH;
    }
    if (joinParamsToPath) {
      config.url = formatParamsToPath(config.url, config.params).url;
    }
    if (config.method === RequestEnum.GET) {
      const now = new Date().getTime();
      if (!isString(config.params)) {
        config.data = {
          // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
          params: Object.assign(config.params || {}, {
            _t: now
          })
        };
      } else {
        // 兼容restful风格
        config.url = config.url + config.params + `?_t=${now}`;
        config.params = {};
      }
    } else {
      if (!isString(config.params)) {
        config.data = config.params;
        config.params = {};
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(config.url as string, config.data);
        }
      } else {
        // 兼容restful风格
        config.url = config.url + config.params;
        config.params = {};
      }
      // 'a[]=b&a[]=c'
      if (!isParseToJson) {
        config.params = qs.stringify(config.params, {
          arrayFormat: 'brackets'
        });
        config.data = qs.stringify(config.data, { arrayFormat: 'brackets' });
      }
    }
    return config;
  },

  /**
   * @description: 请求拦截器处理
   */
  requestInterceptors: config => {
    // 请求之前处理config
    const { info } = storeToRefs(appStore.useUserStoreSetup);
    const token = info.value[TOKEN_KEY];
    if (token) {
      // jwt token
      config.headers[TOKEN_KEY] = token;
    }
    return config;
  },
  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: (error: any) => {
    const isCancel = axios.isCancel(error); // 请求是否被取消
    if (isCancel) {
      console.warn(error, '请求被取消！');
      return Promise.reject({ axiosCancel: true });
    }
    return Promise.reject(error.response ?? error);
  },
  /**
   * @description: 响应数据处理。拦截了data.code不成功抛出异常，返回体与AxiosError保持一致。此处reject后会再次走responseInterceptorsCatch
   */
  responseInterceptors: (resp: any) => {
    const { data, request } = resp;
    // json数据流，data中会有code判断
    if (request.responseType === 'json') {
      // yapi mock接口特殊处理
      if (request?.responseURL?.includes('yapi.jimijiayuan.cn')) {
        if (data.code === void 0) return Promise.reject({ response: resp });
      } else {
        if (data.code !== ResultCodeEnum.SUCCESS) {
          return Promise.reject({ response: resp });
        }
      }
    }
    // 文件流
    if (request.responseType === 'blob') {
      // 无文件
      if (!resp.data?.size) {
        return Promise.reject({ response: resp });
      }
    }
    return resp;
  },

  /**
   * @description: 处理请求数据
   */
  transformRequestData: (resp, options: RequestOptions) => {
    const {
      isShowSuccessMessage,
      successMessageText,
      successMessageMode,
      isTransformRequestResult
    } = options;
    const { data } = resp;
    // 如果data.data无值，部分接口就不返回.data字段了。在这里补一层减少使用层多加一个判断
    if (data?.data === void 0) {
      data.data = null;
    }
    let msg = successMessageText; // 不要使用resp.data.message。优先使用自定义
    if (!msg) {
      // 兼容数据处理
      if (isString(data?.data)) {
        msg = data.data;
      }
      // 兼容数据处理
      if (data?.data?.message) {
        msg = data.data.message;
      }
      msg = msg || '操作成功';
    }

    if (isShowSuccessMessage) {
      if (successMessageMode === 'modal') {
        XrkMessageBox.confirm(msg, '提示');
      } else {
        XrkMessage.success(msg);
      }
    }
    // 统一数据处理
    if (isTransformRequestResult) {
      // 减少一层结构，方便使用
      return data;
    }
    return resp;
  },
  /**
   * @description: 请求失败处理
   */
  requestCatch: async (error, options: RequestOptions) => {
    if ((error as any)?.axiosCancel) return Promise.reject(error);

    const { isShowErrorMessage, errorMessageText, errorMessageMode } = options;
    const { status, data, request } = error;
    // 异常的错误信息与成功的结构单独处理
    const msg = errorMessageText || data?.message || '请求异常，请检查当前网络';

    if (status === void 0) {
      XrkMessage.error('请求异常，请检查当前网络');
      return Promise.reject(error);
    }
    // blob类型异常处理
    if (request.responseType === 'blob') {
      try {
        // 尝试解析错误信息
        const readResult = (await readAsTextCust(error.data)) as Result<any>;
        XrkMessage.error(
          errorMessageText || readResult.message || '下载失败！'
        );
        return Promise.reject({ ...error, data: readResult });
      } catch (e) {
        XrkMessage.error('下载失败！');
        return Promise.reject(error);
      }
    }

    if (isShowErrorMessage) {
      if (errorMessageMode === 'modal') {
        XrkMessageBox.confirm(msg, '错误提示');
      } else {
        XrkMessage.error(msg);
      }
    }
    // 登录过期
    if (
      data.code === ResultCodeEnum.LOGIN &&
      status === ResultStatusEnum.LOGIN
    ) {
      singletonLogout();
    }
    return Promise.reject(error);
  }
};

const Axios = new VAxios({
  timeout: 1000 * 60 * 5,
  // 基础接口地址
  baseURL: REQUEST_PATH,
  // 接口可能会有通用的地址部分，可以统一抽取出来
  // prefixUrl: prefix,
  headers: {
    'Content-Type': ContentTypeEnum.JSON,
    'X-Service-From': PLATFORM_PORT,
    'X-Referer': location.origin,
    'X-SaaS-Platform': PLATFORM_ID
  },
  // 数据处理方式
  transform,
  responseType: 'json',
  // 配置项，下面的选项都可以在独立的接口请求中覆盖
  requestOptions: {
    // 默认将prefix 添加到url
    joinPrefix: true,
    // 需要对返回数据进行处理
    isTransformRequestResult: true,
    // post请求的时候添加参数到url
    joinParamsToUrl: false,
    // 请求的时候添加参数到对应的path
    joinParamsToPath: true,
    isParseToJson: true,
    // 格式化提交参数时间
    formatDate: true,
    // 消息提示类型
    errorMessageMode: 'none',
    isShowErrorMessage: true
  }
});

export default Axios;
